import {ColGroupDef} from 'ag-grid-enterprise';
import { formatDistanceToNow } from 'date-fns';
import {Enums, toTimezoneAndFormat, formatDuration, HelpTooltip} from 'Mri.Web.Common';
import * as React from 'react';
import { ActiveDisplay } from '../../ActiveDisplay';
import { AddressField, AlertsCountField, ContractField, EventsCountField, IDisplayFieldProps, InfiniteTouch1Field, InfiniteTouch2Field, PopVuSide1Field, PopVuSide2Field, SerialNumberField, SoftwareUpdateStatusField, TicketsCountField } from './DisplayFields';
import { stringComparator } from './FieldComparators';
import {SerialNumberFieldDropdown} from "./SerialNumberFieldDropdown";
import {getStatusColor} from "../../DisplayCells";
import {AccountStore} from "../../../Account/AccountStore";

export function getDisplayStatusFields(accountStore: AccountStore): ColGroupDef<ActiveDisplay>[] {
    const defaultCellClass = accountStore.storage.gridCenterAlignColumns ? 'ag-center-cols-cell' : '';
    
    return [
        {
            headerName: 'General',
            children: [
                {
                    headerName: "#",
                    colId: "rowNum",
                    valueGetter: d => d.node!.rowIndex! + 1,
                    initialHide: true,
                    sortable: false,
                },
                {
                    field: "serialNumber",
                    cellEditorPopup: true,
                    editable: true,
                    cellRenderer: SerialNumberField,
                    cellEditor: SerialNumberFieldDropdown,
                    initialHide: true,
                },
                {
                    field: "latestSnapshot1" as any,
                    headerName: "PopVu® 1",
                    cellRenderer: PopVuSide1Field,
                    initialHide: true,
                    sortable: false,
                },
                {
                    field: "latestSnapshot2" as any,
                    headerName: "PopVu® 2",
                    cellRenderer: PopVuSide2Field,
                    initialHide: true,
                    sortable: false

                },
                {
                    field: "latestSnapshot3" as any,
                    headerName: "InfiniteTouch® 1",
                    cellRenderer: InfiniteTouch1Field,
                    initialHide: true,
                    sortable: false

                },
                {
                    field: "latestSnapshot4" as any,
                    headerName: "InfiniteTouch® 2",
                    cellRenderer: InfiniteTouch2Field,
                    initialHide: true,
                    sortable: false
                },
                {
                    field: "unitSerialNumber",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data ? props.data.unitSerialNumber : '-'}</div>,
                    initialHide: true,
                },
                {
                    field: "roleInUnit",
                    filterParams: {
                        valueFormatter: (params: any) => Enums.toString(Enums.RoleInUnit, params.value)
                    },
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data && props.data.roleInUnit ? Enums.toString(Enums.RoleInUnit, props.data.roleInUnit) : '-'}</div>,
                    comparator: (a: Mri.DisplayApi.RoleInUnit, b: Mri.DisplayApi.RoleInUnit) => {
                        let aString = a ? Enums.toString(Enums.RoleInUnit, a) : '';
                        let bString = b ? Enums.toString(Enums.RoleInUnit, b) : '';

                        return stringComparator(aString, bString);
                    },
                    initialHide: true
                },
                {
                    field: "displayConnectionData.customerDisplayID",
                    headerName: "Display ID",
                    editable: true,
                    cellClass: `${defaultCellClass} editable-grid-cell`,
                    tooltipField: "displayConnectionData.customerDisplayID",
                    initialHide: true
                },
                {
                    field: "contractData.serviceLevel",
                    headerName: "Service Level",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data ? Enums.toString(Enums.ServiceLevel, props.data.contractData?.serviceLevel) : '-'}</div>,
                    filterParams: {
                        valueFormatter: (params: any) => Enums.toString(Enums.ServiceLevel, params.value)
                    },
                    initialHide: true
                },
                {
                    field: "contractData.advancedWarrantyAlerts",
                    headerName: "Advanced Warranty Alerts",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data ? (props.data.contractData?.advancedWarrantyAlerts ? 'Included' : 'Not Included') : '-'}</div>,
                    filterParams: {
                        valueFormatter: (params: any) => params.value ? "Included" : "Not Included",
                    },
                    initialHide: true
                },
                {
                    field: "unitLocation.customerSiteID",
                    headerName: "Site ID",
                    editable: true,
                    cellClass: `${defaultCellClass} editable-grid-cell`,
                    initialHide: true
                },
                {
                    field: "accountName",
                    headerName: "Account",
                    initialHide: true
                },
                {
                    field: "comments",
                    editable: true,
                    cellClass: `${defaultCellClass} editable-grid-cell`,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    tooltipField: "comments",
                    initialHide: true,
                    sortable: false
                },
                {
                    field: "openTicketCount",
                    headerName: "Tickets",
                    cellRenderer: TicketsCountField,
                    initialHide: true
                },
                {
                    field: "displayAlertCount",
                    headerName: "Alerts",
                    cellRenderer: AlertsCountField,
                    initialHide: true
                },
                {
                    field: "newDisplayEventCount",
                    headerName: "Events",
                    cellRenderer: EventsCountField,
                    initialHide: true
                },
                {
                    field: "statusMessage",
                    headerName: "Status",
                    tooltipField: "statusMessage",
                    initialHide: true,
                    cellRenderer: (props: IDisplayFieldProps) =>
                        <div
                            className={props.data ? getStatusColor(props.data, false) + ' d-block h-100' : ''}
                        >
                            {props.data &&
                                <div className="d-inline-block w-100">
                                    {props.data.statusMessage}
                                </div>
                            }
                        </div>
                },
            ]
        },
        {
            headerName: 'System',
            children: [
                {
                    field: "webApiKickDisabled",
                    headerName: "WebApi Kicks",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data?.webApiKickDisabled ? 'Disabled' : 'Enabled'}</div>,
                    filterParams: {
                        valueFormatter: (params: any) => params.value ? "Enabled" : "Disabled"
                    },
                    initialHide: true
                },
                {
                    field: "unitLocation.address1",
                    headerName: "Address",
                    cellRenderer: AddressField,
                    tooltipField: "unitLocation.address1",
                    initialHide: true
                },
                {
                    field: "unitLocation.city",
                    headerName: "City",
                    initialHide: true
                },
                {
                    field: "unitLocation.stateOrProvince",
                    headerName: "State Or Province",
                    initialHide: true
                },
                {
                    field: "unitLocation.country",
                    headerName: "Country",
                    initialHide: true
                },
                {
                    field: "uptime",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data && props.data.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect ? '-' : (props.data && props.data.uptime ? formatDuration(props.data.uptime) : '-')}</div>,
                    initialHide: true
                },
                {
                    field: "shippedDate",
                    cellRenderer: (props: IDisplayFieldProps) => <div>{props.data && props.data.shippedDate ? toTimezoneAndFormat(props.data.shippedDate, 'MM/dd/yyyy', 'UTC') : '-'}</div>,
                    initialHide: true
                },
                {
                    field: "contract",
                    cellRenderer: ContractField,
                    initialHide: true
                },
                {
                    field: "pollTime",
                    headerName: "Last Poll Time",
                    cellRenderer: (props: IDisplayFieldProps) => {
                        const pollTime = props.data?.pollTime;
                        const message = pollTime
                            ? pollTime < new Date()
                                ? formatDistanceToNow(pollTime, { addSuffix: true })
                                : '< 1 min'
                            : '-';

                        return (
                            <div>
                                {message}
                            </div>
                        );
                    },
                    initialHide: true
                },
                {
                    field: "displayConnectionData.nocServer",
                    headerName: "Server",
                    initialHide: true
                },
                {
                    field: "displayConnectionData.displayBoardIP",
                    headerName: "IP Address",
                    editable: true,
                    cellClass: `${defaultCellClass} editable-grid-cell`,
                    initialHide: true
                },
                {
                    field: "displaySoftwareUpdateStatus.state",
                    headerName: "Update Status",
                    cellRenderer: SoftwareUpdateStatusField,
                    comparator: (a: ActiveDisplay, b: ActiveDisplay) => {
                        let statusA = a.displaySoftwareUpdateStatus;
                        let statusB = b.displaySoftwareUpdateStatus;
                        if (statusA && statusB) {
                            return statusA.state! - statusB.state!;
                        }

                        return -1;
                    },
                    filterParams: {
                        valueFormatter: (params: any) => params.value != undefined ? Enums.toString(Enums.SoftwareUpdateState, params.value) : 'None'
                    },
                    initialHide: true
                },
                {
                    field: "partNumber",
                    tooltipField: "partNumber",
                    initialHide: true
                },
                {
                    field: "modelNumber",
                    tooltipField: "modelNumber",
                    initialHide: true
                },
            ]
        },
    ];
}
